import React, { useEffect, useRef, useState } from 'react';
import { Template1ResumeData, flattenSkills, filterNullEntries } from './Template1';
import { ResumePaginator, PageMargins } from '@/utils/resumePagination';

interface PaginatedTemplate4Props {
  data: Template1ResumeData;
  margins?: PageMargins;
}

export const PaginatedTemplate4: React.FC<PaginatedTemplate4Props> = ({ data, margins }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paginatedContent, setPaginatedContent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const sections: HTMLElement[] = [];

    // Header Section - Elegant Style
    const header = document.createElement('header');
    header.className = 'text-center border-b-2 border-emerald-600 pb-3 mb-3';
    header.setAttribute('data-keep-together', 'true');
    header.innerHTML = `
      <h1 class="text-3xl font-serif font-bold text-emerald-900 mb-2">${data.Name}</h1>
      <div class="text-sm text-gray-700 space-x-2">
        <span>${data.Email}</span>
        <span>•</span>
        <span>${data.Phone}</span>
        <span>•</span>
        <span>${data.City}, ${data.State}</span>
      </div>
      ${data.LinkedIn ? `<div class="text-sm text-emerald-700 mt-1">${data.LinkedIn}</div>` : ''}
    `;
    sections.push(header);

    // Summary Section
    const summary = document.createElement('section');
    summary.className = 'mb-3 bg-emerald-50 p-3 rounded';
    summary.innerHTML = `
      <h2 class="text-sm font-bold text-emerald-900 mb-2 uppercase tracking-wide">Professional Summary</h2>
      <p class="text-xs text-gray-800 leading-relaxed">${data.Summary}</p>
    `;
    sections.push(summary);

    // Skills Section - Support both SkillRows and Skills formats
    const allSkills = flattenSkills(data);
    if (allSkills.length > 0) {
      const skills = document.createElement('section');
      skills.className = 'mb-3';
      const skillsHtml = allSkills.map(s => `
        <span class="inline-block bg-emerald-100 text-emerald-900 px-3 py-1 rounded-full text-xs mr-2 mb-2">${s}</span>
      `).join('');
      skills.innerHTML = `
        <h2 class="text-sm font-bold text-emerald-900 mb-2 uppercase tracking-wide border-b border-emerald-300 pb-1">Areas of Expertise</h2>
        <div class="flex flex-wrap">${skillsHtml}</div>
      `;
      sections.push(skills);
    }

    // Career Highlights
    if (data.CareerHighlights && data.CareerHighlights.length > 0) {
      const highlights = document.createElement('section');
      highlights.className = 'mb-3';
      const highlightsHtml = data.CareerHighlights.map(h => `
        <div class="mb-2 bg-emerald-50 p-2 rounded">
          <p class="text-xs text-gray-800 leading-snug">
            <span class="font-bold text-emerald-900">${h.chtitle}:</span> ${h.chdescription}
          </p>
        </div>
      `).join('');
      highlights.innerHTML = `
        <h2 class="text-sm font-bold text-emerald-900 mb-2 uppercase tracking-wide border-b border-emerald-300 pb-1">Notable Achievements</h2>
        ${highlightsHtml}
      `;
      sections.push(highlights);
    }

    // Professional Experience
    if (data.ProfessionalExperience && data.ProfessionalExperience.length > 0) {
      const firstJob = data.ProfessionalExperience[0];
      const expBlock = document.createElement('section');
      expBlock.className = 'mb-3';
      expBlock.setAttribute('data-keep-together', 'true');

      const firstBullets = firstJob.Descriptions.map(desc => 
        `<li class="leading-snug text-xs text-gray-800">${desc.Description}</li>`
      ).join('');

      expBlock.innerHTML = `
        <h2 class="text-sm font-bold text-emerald-900 mb-3 uppercase tracking-wide border-b border-emerald-300 pb-1">Professional Experience</h2>
        <div class="mb-3">
          <div class="flex justify-between items-baseline mb-1">
            <h3 class="font-bold text-sm text-emerald-900">${firstJob.company}</h3>
            <span class="text-xs text-gray-600 italic">${firstJob.start_date} - ${firstJob.end_date}</span>
          </div>
          <p class="text-xs font-semibold text-gray-800 mb-1">${firstJob.job_title}</p>
          ${firstJob.location ? `<p class="text-xs text-gray-600 mb-2">${firstJob.location}</p>` : ''}
          <ul class="list-none ml-2 space-y-1">
            ${firstJob.Descriptions.map(desc => 
              `<li class="text-xs text-gray-800 leading-snug"><span class="text-emerald-600 mr-1">▸</span>${desc.Description}</li>`
            ).join('')}
          </ul>
        </div>
      `;
      sections.push(expBlock);

      data.ProfessionalExperience.slice(1).forEach((job) => {
        const jobSection = document.createElement('section');
        jobSection.className = 'mb-3';
        jobSection.setAttribute('data-experience-continuation', 'true');
        
        jobSection.innerHTML = `
          <div class="mb-3">
            <div class="flex justify-between items-baseline mb-1" data-stick-with-next-list="true">
              <h3 class="font-bold text-sm text-emerald-900">${job.company}</h3>
              <span class="text-xs text-gray-600 italic">${job.start_date} - ${job.end_date}</span>
            </div>
            <p class="text-xs font-semibold text-gray-800 mb-1" data-stick-with-next-list="true">${job.job_title}</p>
            ${job.location ? `<p class="text-xs text-gray-600 mb-2" data-stick-with-next-list="true">${job.location}</p>` : ''}
            <ul class="list-none ml-2 space-y-1">
              ${job.Descriptions.map(desc => 
                `<li class="text-xs text-gray-800 leading-snug"><span class="text-emerald-600 mr-1">▸</span>${desc.Description}</li>`
              ).join('')}
            </ul>
          </div>
        `;
        sections.push(jobSection);
      });
    }

    // Education Section
    const education = document.createElement('section');
    education.className = 'mb-3';
    const eduHtml = data.Education.map(edu => `
      <div class="mb-2">
        <p class="text-xs font-bold text-emerald-900">${edu.College_Name}</p>
        <p class="text-xs text-gray-800">${edu.College_Degree_Name}${edu.Major ? `, ${edu.Major}` : ''}</p>
        ${(edu.Year_Earned || edu.YearEarned) ? `<p class="text-xs text-gray-600">${edu.Year_Earned || edu.YearEarned}</p>` : ''}
      </div>
    `).join('');
    education.innerHTML = `
      <h2 class="text-sm font-bold text-emerald-900 mb-2 uppercase tracking-wide border-b border-emerald-300 pb-1">Education</h2>
      ${eduHtml}
    `;
    sections.push(education);

    // Certifications - Filter out null entries
    const validCertifications = filterNullEntries(data.CertificationsAndLicenses);
    if (validCertifications.length > 0) {
      const certs = document.createElement('section');
      certs.className = 'mb-3';
      const certsHtml = validCertifications.map(cert => `
        <p class="text-xs text-gray-800 mb-1">
          <span class="font-semibold text-emerald-900">${cert.Certification_or_License_Name}</span>
          ${cert.Institution_Organization ? ` | ${cert.Institution_Organization}` : ''}
          ${cert.Year ? ` | ${cert.Year}` : ''}
        </p>
      `).join('');
      certs.innerHTML = `
        <h2 class="text-sm font-bold text-emerald-900 mb-2 uppercase tracking-wide border-b border-emerald-300 pb-1">Certifications</h2>
        ${certsHtml}
      `;
      sections.push(certs);
    }

    const paginator = new ResumePaginator({
      margins: margins || { top: 0.5, bottom: 0.5, left: 0.5, right: 0.5 }
    });

    const tempContainer = document.createElement('div');
    const paginated = paginator.paginate(tempContainer, sections);
    setPaginatedContent(paginated);

  }, [data, margins]);

  return (
    <div ref={containerRef}>
      {paginatedContent && (
        <div 
          className="font-document"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
          dangerouslySetInnerHTML={{ __html: paginatedContent.innerHTML }} 
        />
      )}
    </div>
  );
};
